import i18n from "i18next";

export function compareStrings(str1: string, str2: string) {
  return (
    standardizeStringForComparison(str1) ===
    standardizeStringForComparison(str2)
  );
}
export function standardizeStringForComparison(str: string) {
  return str
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, ""); // Remove characters that aren't letters, numbers, underscores or spaces
}

export function slugify(input?: string, defaultValue = "-") {
  if (!input) {
    return defaultValue;
  }

  return input.toLowerCase().replaceAll(" ", "-").replaceAll("_", "-");
}

export function slugifyPath(input?: string, defaultValue = "-") {
  if (!input) {
    return defaultValue;
  }

  return input
    .toLowerCase()
    .replaceAll(" ", "-")
    .replaceAll("_", "-")
    .replaceAll(".", "-");
}

export function truncateMiddle(text: string) {
  if (text.length > 16) {
    return `${text.slice(0, 8)}...${text.slice(-8)}`;
  }
  return text;
}

export type AbstractName =
  | Record<string, string | null | undefined>
  | string
  | undefined
  | null;

export function parseName(value?: AbstractName): Name {
  if (typeof value === "string") {
    return parseFullName(value);
  }

  return {
    title: value?.title || "",
    givenName:
      value?.given_name ||
      value?.first_name ||
      value?.givenName ||
      value?.firstName ||
      "",
    familyName:
      value?.family_name ||
      value?.last_name ||
      value?.familyName ||
      value?.lastName ||
      "",
    additionalName:
      value?.additional_name ||
      value?.middle_name ||
      value?.additionalName ||
      value?.middleName ||
      "",
    initials: value?.initials || "",
  };
}

export interface Name {
  title?: string;
  givenName?: string;
  familyName?: string;
  additionalName?: string;
  initials?: string;
}

export function formatName(
  name?: Name | null,
  type: "friendly" | "initials" | "formal" | "full" = "friendly",
  defaultValue = "-"
) {
  if (!name) {
    return defaultValue;
  }

  if (type === "initials") {
    if (name.initials) {
      return name.initials;
    }

    return [
      name.givenName && name.givenName[0],
      name.familyName && name.familyName[0],
    ]
      .filter(Boolean)
      .join("")
      .toUpperCase();
  }

  return [
    type !== "friendly" && name.title,
    name.givenName,
    type === "full" && name.additionalName,
    name.familyName,
  ]
    .filter(Boolean)
    .join(" ");
}

export interface Address {
  locality?: string;
  postal_code?: string;
  street_address?: string;
  administrative_division_1?: string;
}

export function formatAddress(address?: Address | null, defaultValue = "-") {
  if (!address) {
    return defaultValue;
  }

  return [
    address.street_address,
    address.locality,
    address.administrative_division_1,
    address.postal_code,
  ]
    .filter(Boolean)
    .join(", ");
}

export function formatPhone(phone?: { number: string }) {
  if (!phone) {
    return "-";
  }

  return `${phone.number}`;
}

const titles = ["mr", "mrs", "ms", "miss", "dr", "prof", "sir", "lady"];

export function parseFullName(fullName?: string): Name {
  const parts = fullName?.split(" ") || [];

  const title = parts.find((part) => titles.includes(part.toLocaleLowerCase()));
  const familyName = parts
    .slice()
    .reverse()
    .find((part) => part !== title);
  const givenName = parts.find((part) => part !== title && part !== familyName);
  const additionalNames = parts.filter(
    (part) => part !== title && part !== givenName && part !== familyName
  );

  return {
    title: title || "",
    givenName: givenName || "",
    familyName: familyName || "",
    additionalName: additionalNames.join(" ") || "",
    initials: [givenName?.charAt(0), familyName?.charAt(0)]
      .filter(Boolean)
      .join("")
      .toUpperCase(),
  };
}

export function getGivenName(fullName?: string) {
  if (!fullName) {
    return "";
  }
  const firstName = fullName.split(" ")[0];

  return firstName;
}

export function getFamilyName(fullName?: string) {
  if (!fullName) {
    return "";
  }

  const parts = fullName.split(" ");
  return parts[parts.length - 1];
}

// for one word strings
export function convertToTitleCase(uppercaseString: string) {
  const lowerCaseString = uppercaseString.toLowerCase();
  const sentenceCaseString =
    lowerCaseString.charAt(0).toUpperCase() + lowerCaseString.slice(1);
  return sentenceCaseString;
}

export function formatLanguage(locale: string | undefined, defaultValue = "-") {
  if (!locale) {
    return defaultValue;
  }

  const formatter = new Intl.DisplayNames(i18n.languages, {
    type: "language",
  });

  return formatter.of(locale);
}

export const defaultTemplateIdentifier = "\\[\\[(.*?)\\]\\]"; // matches [[(.*?)]]
/**
 * @param {string} regexIdentifier - This is the regex patten you want to replace
 * @param {string} value - This is the templated string you want to remove the template tags from
 * @example removeTemplateTags("{{hello}}");
 * returns hello
 */
export function removeTemplateTags(
  value: string,
  regexIdentifier = defaultTemplateIdentifier
) {
  const identifier = new RegExp(regexIdentifier, "g");
  return value.replace(identifier, (_, part) => part);
}

export function inferCurrency(locale: string = "en-US") {
  switch (locale) {
    case "en-CA":
      return "CAD";
    case "en-US":
      return "USD";
    case "en-GB":
      return "GBP";
    case "en-AU":
      return "AUD";
    case "fr-CA":
      return "CAD";
    default:
      return "USD";
  }
}

export function formatCurrency(
  value?: number,
  locale: string = "en-US",
  currency?: string,
  maximumFractionDigits: number = 0
) {
  if (value === undefined || currency === undefined) {
    return "-";
  }
  return Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits,
  }).format(value);
}

export function formatCapitalizedString(value: string | undefined) {
  if (typeof value !== "string") {
    return value;
  }

  value = value.replaceAll("_", " ").toLowerCase();
  return value.length > 0 ? value[0].toUpperCase() + value.slice(1) : "";
}

export function formatDurationLength(
  duration?: { key: string; value: string | number }[],
  defaultValue = "-"
) {
  if (!duration || !Array.isArray(duration) || duration.length === 0) {
    return defaultValue;
  }

  return duration
    .filter((item) => item.value && Number(item.value) > 0)
    .map((item) => `${item.value} ${item.key}`)
    .join(", ");
}

/**
 * Calculates the appropriate timeout duration for toast notifications based on message length.
 * Longer messages need more time for users to read them completely.
 *
 * @param message - The message text to calculate timeout for
 * @returns Timeout duration in milliseconds (minimum 500ms, +250ms per word)
 */
export function calculateToastTimeout(message: string): number {
  const wordCount = message.trim().split(/\s+/).length;
  return 500 + 250 * wordCount;
}
